import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { copyProtectionProps } from "./copy-protection";

/**
 * Frame of a contribution task page.
 *
 * ```tsx
 * <TaskShell header={<TaskHeader task="speak" ... />} notices={<ValidationGateBanner />} actionBar={<TaskActionBar ... />}>
 *   <TaskItemCard ...>...</TaskItemCard>
 * </TaskShell>
 * ```
 *
 * - `header`: the compact TaskHeader.
 * - `notices`: banners above the item (validation gate, re-record reason...).
 * - `children`: the item to handle, usually one TaskItemCard and its side panel.
 * - `actionBar`: a TaskActionBar. It sticks to the bottom of the screen (above
 *   the phone bottom nav) and rests under the content at the end of the page,
 *   so it never hides the last lines.
 * - `width`: "narrow" (max-w-3xl, one column) or "wide" (max-w-5xl, room for
 *   a side panel).
 *
 * The page content cannot be selected, copied or dragged out (fields excepted):
 * the rest of the site stays selectable.
 */
export function TaskShell({
  header,
  notices,
  actionBar,
  children,
  width = "narrow",
  className,
}: {
  header: ReactNode;
  notices?: ReactNode;
  actionBar?: ReactNode;
  children: ReactNode;
  width?: "narrow" | "wide";
  className?: string;
}) {
  const max = width === "wide" ? "max-w-5xl" : "max-w-3xl";
  return (
    <div className={cn("copy-protected flex min-h-[calc(100dvh-4.5rem)] flex-col bg-background", className)} {...copyProtectionProps}>
      <div className={cn("mx-auto w-full flex-1 space-y-5 px-4 pb-8 pt-5 sm:px-6 sm:pt-6", max)}>
        {header}
        {notices ? <div className="space-y-3">{notices}</div> : null}
        {children}
      </div>
      {actionBar ? (
        <div className="sticky bottom-[calc(env(safe-area-inset-bottom)_+_5.75rem)] z-30 mt-auto border-t border-border/60 bg-background md:bottom-0">
          <div className={cn("mx-auto w-full px-4 py-3 sm:px-6", max)}>{actionBar}</div>
        </div>
      ) : null}
    </div>
  );
}
